import { useFetcher } from 'react-router';
import { useCallback, useEffect, useRef } from 'react';
import type { TagDataUseToCreateTag } from '@modules/product-tagging/type';
import type { UpdateLibraryImageMarkerMutation } from 'generated/graphql';
import generateFetcherKey from '~/utils/generate-fetcher-key';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { toast } from 'sonner';

interface UseEditTagResult {
    editTag: (markerID: number, data: TagDataUseToCreateTag) => void;
    isSubmitting: boolean;
}

/**
 * Submits an edit-tag request via the existing update-library-image-marker route.
 * Replaces the product/supplier linkage on an existing marker while keeping
 * position and original creator intact.
 */
const useEditTag = (onComplete?: () => void): UseEditTagResult => {
    const fetcher = useFetcher<UpdateLibraryImageMarkerMutation>();
    const submitRef = useRef(fetcher.submit);
    submitRef.current = fetcher.submit;

    useEffect(() => {
        if (fetcher.state === 'loading') {
            toast('Tag updated', { duration: 3000 });
        }
        if (fetcherIsDone(fetcher) && fetcher.data?.updateLibraryImageMarker) {
            onComplete?.();
        }
    }, [fetcher, onComplete]);

    const editTag = useCallback(
        (markerID: number, data: TagDataUseToCreateTag) => {
            const formData = new FormData();
            formData.set('tagId', markerID.toString());
            formData.set('fetcherKey', generateFetcherKey());

            const targetID = data.product
                ? data.product.id
                : data.professionalForUnknownProduct?.id;

            if (targetID) {
                formData.set('targetID', targetID.toString());
            }

            const unknownProfessional =
                data.titleForUnknownProfessional?.trim();
            if (unknownProfessional) {
                formData.set('professionalName', unknownProfessional);
            }

            if (data.titleForUnknownProduct) {
                formData.set(
                    'titleForUnknownProduct',
                    data.titleForUnknownProduct,
                );
            }
            if (data.urlForUnknownProduct) {
                formData.set('urlForUnknownProduct', data.urlForUnknownProduct);
            }

            submitRef.current(formData, {
                method: 'post',
                action: '/remix-api/product-tagging/update-library-image-marker',
                preventScrollReset: true,
            });
        },
        [],
    );

    return {
        editTag,
        isSubmitting: fetcher.state !== 'idle',
    };
};

export default useEditTag;
